<template>
    <Head title="Add Property Details - RentRisk" />

    <GuestLayout v-if="!isAuthenticated">
        <div class="min-h-screen bg-white py-8 px-4 sm:px-5 lg:px-6">
            <div class="max-w-6xl mx-auto">
                <div class="mb-6 flex justify-start">
                    <jet-authentication-card-logo-colored />
                </div>
                <PropertyHeroBand
                    v-if="isEditMode && existingProperty"
                    :property="existingProperty"
                    class="mb-6"
                />
                <AddPropertyForm
                    :form="form"
                    :autocompleteRef="autocompleteRef"
                    :initAutocomplete="initAutocomplete"
                    :formatNumber="formatNumber"
                    :propertyStructureTypes="propertyStructureTypes"
                    :isEditMode="isEditMode"
                    :property="existingProperty"
                    :screening-fee-amount="screeningFeeAmount"
                    :show-application-fee-section="showApplicationFeeSection"
                    :has-payment-method="paymentMethodState"
                    :has-stripe-setup="hasStripeSetup"
                    :payment-method-success="paymentMethodSuccess"
                    @submit="submit"
                />
            </div>
        </div>
    </GuestLayout>

    <TailwindLayout v-else title="Add Property Details">
        <div class="max-w-6xl mx-auto">
            <PropertyHeroBand
                v-if="isEditMode && existingProperty"
                :property="existingProperty"
                class="mb-6"
            />
            <AddPropertyForm
                :form="form"
                :autocompleteRef="autocompleteRef"
                :initAutocomplete="initAutocomplete"
                :formatNumber="formatNumber"
                :propertyStructureTypes="propertyStructureTypes"
                :isEditMode="isEditMode"
                :property="existingProperty"
                :screening-fee-amount="screeningFeeAmount"
                :show-application-fee-section="showApplicationFeeSection"
                :has-payment-method="paymentMethodState"
                :has-stripe-setup="hasStripeSetup"
                :payment-method-success="paymentMethodSuccess"
                @submit="submit"
            />
        </div>
    </TailwindLayout>

    <AddCardPopup
        v-if="showApplicationFeeSection && normalizedStripeData"
        :show="showAddCardModal"
        :stripe_data="normalizedStripeData"
        @card-added="handleCardAdded"
        @close="closeCardModal"
    />

    <!-- 86bcd9hg3 (decision 6): adding the property that moves an account-wide Pro
         landlord up a tier shows the new yearly price before the property is saved. -->
    <ProTierChangeNoticeModal
        :show="showTierChangeNotice"
        :processing="form.processing"
        :change="nextPropertyTierChange ?? undefined"
        @close="showTierChangeNotice = false"
        @confirm="confirmTierChangeNotice"
    />
</template>

<script setup>
import { Head, useForm, usePage } from '@inertiajs/vue3'
import GuestLayout from '@/Layouts/V2/GuestLayout.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import JetAuthenticationCardLogoColored from '@/Jetstream/AuthenticationCardLogoColored.vue'
import { computed, ref, watch } from 'vue'
import AddPropertyForm from './AddPropertyForm.vue'
import PropertyHeroBand from './Components/PropertyHeroBand.vue'
import AddCardPopup from '@/Shared/AddCardPopup.vue'
import ProTierChangeNoticeModal from '@/Components/ProTierChangeNoticeModal.vue'
import { parseGooglePlaceAddress } from './parseGooglePlaceAddress'

const page = usePage()
const isAuthenticated = computed(() => !!page.props.auth?.user)
const propertyStructureTypes = computed(() => page.props.propertyStructureTypes || {})
const isEditMode = computed(() => page.props.isEditMode === true)
const existingProperty = computed(() => page.props.property || null)

const showApplicationFeeSection = computed(() => {
    const u = page.props.auth?.user
    if (u) {
        return u.role === 'agent'
    }
    const pending = page.props.pendingUser
    return pending?.role === 'agent'
})

const userPaymentMethodProp = computed(() => page.props.user_payment_method ?? null)
const nextPropertyTierChange = computed(() => page.props.auth?.user?.pro_plan?.next_property_tier_change ?? null)
const stripeDataProp = computed(() => page.props.stripe_data ?? null)

const normalizedStripeData = computed(() => {
    const d = stripeDataProp.value
    if (!d || Array.isArray(d) || typeof d !== 'object') {
        return null
    }
    return d.publishable_key ? d : null
})

const hasStripeSetup = computed(() => normalizedStripeData.value !== null)

const screeningFeeAmount = computed(() => {
    const raw = page.props.screening_fee_amount
    const n = Number(raw)
    return Number.isFinite(n) ? n : 59
})

const paymentMethodState = ref(!!userPaymentMethodProp.value)

watch(
    userPaymentMethodProp,
    (pm) => {
        paymentMethodState.value = !!pm
    },
    { immediate: true }
)

const showAddCardModal = ref(false)
const paymentMethodSuccess = ref(null)

const autocompleteRef = ref(null)

const defaultIsAgentFeeFromAccount = () => {
    const u = page.props.auth?.user
    if (u && Number(u.is_agent_fee_all) === 1) {
        return true
    }
    return false
}

const getInitialFormData = () => {
    if (isEditMode.value && existingProperty.value) {
        return {
            owner_name: existingProperty.value.owner_name || null,
            address_1: existingProperty.value.address_1 || null,
            address_2: existingProperty.value.address_2 || null,
            city: existingProperty.value.city || null,
            state: existingProperty.value.state || null,
            county: existingProperty.value.county || null,
            zip_code: existingProperty.value.zip_code || null,
            latitude: existingProperty.value.latitude ? Number(existingProperty.value.latitude) : null,
            longitude: existingProperty.value.longitude ? Number(existingProperty.value.longitude) : null,
            rent: existingProperty.value.rent || null,
            deposit: existingProperty.value.deposit || null,
            lease_term: existingProperty.value.lease_term || null,
            availability_date: existingProperty.value.availability_date || null,
            type: existingProperty.value.type || null,
            property_structure_type: existingProperty.value.property_structure_type || null,
            sq_ft: existingProperty.value.sq_ft || null,
            beds: existingProperty.value.beds || null,
            baths: existingProperty.value.baths || null,
            status: existingProperty.value.status ?? 1,
            screening_plan: existingProperty.value.screening_plan ?? 0,
            ca_icraa_compliance: existingProperty.value.ca_icraa_compliance ?? false,
            // DB / JSON use 0/1; radio inputs use boolean :value so coerce for v-model match
            is_agent_fee: [1, true, '1'].includes(existingProperty.value.is_agent_fee),
            is_renters_insurance: existingProperty.value.is_renters_insurance ?? true,
        }
    }
    return {
        owner_name: null,
        address_1: null,
        address_2: null,
        city: null,
        state: null,
        county: null,
        zip_code: null,
        latitude: null,
        longitude: null,
        rent: null,
        deposit: null,
        lease_term: null,
        availability_date: null,
        type: null,
        property_structure_type: null,
        sq_ft: null,
        beds: null,
        baths: null,
        status: 1,
        screening_plan: 0,
        ca_icraa_compliance: false,
        is_agent_fee: defaultIsAgentFeeFromAccount(),
        is_renters_insurance: true,
    }
}

const form = useForm(getInitialFormData())

watch(
    () => form.is_agent_fee,
    (isAgent) => {
        if (!showApplicationFeeSection.value) {
            return
        }
        if (isAgent && !paymentMethodState.value && normalizedStripeData.value) {
            form.clearErrors('add_card')
            showAddCardModal.value = true
        }
    }
)

const formatNumber = (num) => {
    if (!num) {
        return ''
    }
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const closeCardModal = () => {
    showAddCardModal.value = false
    form.clearErrors('add_card')
    if (paymentMethodState.value) {
        return
    }
    if (isEditMode.value && existingProperty.value && Boolean(existingProperty.value.is_agent_fee)) {
        form.is_agent_fee = true
    } else {
        form.is_agent_fee = false
    }
}

const handleCardAdded = (payload) => {
    if (!payload.success) {
        form.setError('add_card', payload.error || 'Failed to add card. Please try again.')
        return
    }
    form.clearErrors('add_card')
    showAddCardModal.value = false
    paymentMethodState.value = true
    paymentMethodSuccess.value = 'Card added successfully.'
    setTimeout(() => {
        paymentMethodSuccess.value = null
    }, 3000)
}

const initAutocomplete = (place) => {
    // Google Places sometimes returns a `place` without address_components when
    // the user picks an "establishment" / "regions" / area-level result. We
    // require a structured address (esp. zipcode) for FCRA compliance — some
    // cities/counties trigger a 2-step share requirement, so manual entry is
    // not an option. Show an inline error and bail; user must pick a more
    // specific result. RAC-9989 tracks the USPS-fallback strategic work.
    if (!place?.address_components) {
        form.errors.address =
            'We couldn\'t read a full address from that result. Please pick a more specific result from the dropdown — make sure it\'s a street address, not a business or area.'
        return
    }
    form.errors.address = null

    const parsed = parseGooglePlaceAddress(place.address_components)

    // The picker owns the structured address, so city always follows the pick —
    // including back to null when the result carries no locality at all.
    form.city = parsed.city

    if (parsed.zip_code !== null) {
        form.zip_code = parsed.zip_code
    }
    if (parsed.county !== null) {
        form.county = parsed.county
    }
    if (parsed.state !== null) {
        form.state = parsed.state
    }

    form.address_1 = parsed.address_1
    if (parsed.address_1 === null) {
        form.errors.address_1 = 'Address 1 not found. Enter the full address in Google search.'
    } else {
        form.errors.address_1 = null
    }

    // Google only returns a unit when the picked suggestion carries one, which it
    // usually doesn't — so a missing unit must never clear one the user typed by
    // hand into the Address 2 field.
    if (parsed.unit !== null) {
        form.address_2 = parsed.unit
        form.errors.address_2 = null
    }

    if (place.geometry && place.geometry.location) {
        const location = place.geometry.location
        form.latitude = typeof location.lat === 'function' ? location.lat() : location.lat
        form.longitude = typeof location.lng === 'function' ? location.lng() : location.lng
    }

    if (autocompleteRef.value && autocompleteRef.value.$refs && autocompleteRef.value.$refs.input) {
        autocompleteRef.value.$refs.input.value = null
    }
}

const buildSubmitPayload = () => {
    const safeParseInt = (value) => {
        if (value === null || value === '' || value === undefined) {
            return null
        }
        const parsed = parseInt(value, 10)
        return isNaN(parsed) ? null : parsed
    }

    const safeParseFloat = (value) => {
        if (value === null || value === '' || value === undefined) {
            return null
        }
        const parsed = parseFloat(value)
        return isNaN(parsed) ? null : parsed
    }

    if (form.rent !== null && form.rent !== '') {
        form.rent = Number(form.rent)
    }
    if (form.deposit !== null && form.deposit !== '') {
        form.deposit = Number(form.deposit)
    }
    if (form.beds !== null && form.beds !== '') {
        form.beds = safeParseInt(form.beds)
    }
    if (form.baths !== null && form.baths !== '') {
        form.baths = safeParseFloat(form.baths)
    }
    if (form.sq_ft !== null && form.sq_ft !== '') {
        form.sq_ft = safeParseFloat(form.sq_ft)
    }
    if (form.latitude !== null && form.latitude !== '') {
        form.latitude = safeParseFloat(form.latitude)
    }
    if (form.longitude !== null && form.longitude !== '') {
        form.longitude = safeParseFloat(form.longitude)
    }
    if (form.property_structure_type !== null && form.property_structure_type !== '') {
        form.property_structure_type = safeParseInt(form.property_structure_type)
    }

    if (form.address_1 === '') {
        form.address_1 = null
    }
    if (form.city === '') {
        form.city = null
    }
    if (form.state === '') {
        form.state = null
    }
    if (form.zip_code === '') {
        form.zip_code = null
    }
    if (form.availability_date === '') {
        form.availability_date = null
    }

    return {
        owner_name: form.owner_name || null,
        address_1: form.address_1 || null,
        address_2: form.address_2 || null,
        county: form.county || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        latitude: form.latitude !== null && form.latitude !== undefined ? form.latitude : null,
        longitude: form.longitude !== null && form.longitude !== undefined ? form.longitude : null,
        property_structure_type:
            form.property_structure_type !== null && form.property_structure_type !== undefined
                ? form.property_structure_type
                : null,
        availability_date: form.availability_date || null,
        rent: form.rent !== null && form.rent !== undefined ? form.rent : null,
        deposit: form.deposit !== null && form.deposit !== undefined ? form.deposit : null,
        lease_term: form.lease_term !== null && form.lease_term !== undefined ? form.lease_term : null,
        sq_ft: form.sq_ft !== null && form.sq_ft !== undefined ? form.sq_ft : null,
        beds: form.beds !== null && form.beds !== undefined ? form.beds : null,
        baths: form.baths !== null && form.baths !== undefined ? form.baths : null,
        status: form.status ?? 1,
        screening_plan: form.screening_plan ?? 0,
        ca_icraa_compliance: form.ca_icraa_compliance ?? false,
        is_agent_fee: form.is_agent_fee ?? false,
        is_renters_insurance: form.is_renters_insurance ?? true,
    }
}

const showTierChangeNotice = ref(false)
const tierChangeNoticeConfirmed = ref(false)

const confirmTierChangeNotice = () => {
    showTierChangeNotice.value = false
    tierChangeNoticeConfirmed.value = true
    submit()
}

const submit = () => {
    if (isEditMode.value && (!existingProperty.value || !existingProperty.value.id)) {
        if (import.meta.env.DEV) {
            console.error('Edit mode but no property ID found!', {
                isEditMode: isEditMode.value,
                existingProperty: existingProperty.value,
            })
        }
        alert('Error: Property ID not found. Please refresh the page and try again.')
        return
    }

    if (showApplicationFeeSection.value && form.is_agent_fee && !paymentMethodState.value) {
        if (normalizedStripeData.value) {
            form.clearErrors('add_card')
            showAddCardModal.value = true
        }
        return
    }

    if (!isEditMode.value && nextPropertyTierChange.value && !tierChangeNoticeConfirmed.value) {
        showTierChangeNotice.value = true
        return
    }

    const data = buildSubmitPayload()
    Object.keys(data).forEach((key) => {
        if (data[key] !== undefined) {
            form[key] = data[key]
        }
    })

    const submitRoute = isEditMode.value
        ? window.route('properties.update', existingProperty.value.id)
        : window.route('add-property.store')

    const onError = () => {
        tierChangeNoticeConfirmed.value = false
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    if (isEditMode.value) {
        form.put(submitRoute, { preserveScroll: false, onError })
    } else {
        form.post(submitRoute, { preserveScroll: false, onError })
    }
}
</script>
